import { FormEvent, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import AuthShell from '../../components/auth/AuthShell';
import { useAuth } from '../../contexts/AuthContext';
import { SUBUSER_INVITE_QUERY } from '../../graphql/queries';
import { ACCEPT_SUBUSER_INVITE_MUTATION } from '../../graphql/mutations';
import styles from './AcceptInvite.module.css';
import Icon from '../../components/common/Icon';

// Where a subuser's invitation email lands: they choose a password and are
// signed straight in.
const AcceptInvite = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const navigate = useNavigate();
  const { login, logout, isAuthenticated } = useAuth();
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState('');

  const { data, loading: checking, error: checkError } = useQuery(SUBUSER_INVITE_QUERY, {
    variables: { token },
    skip: !token,
    fetchPolicy: 'network-only',
  });
  const invite = data?.subuserInvite;

  const [accept, { loading: saving }] = useMutation(ACCEPT_SUBUSER_INVITE_MUTATION, {
    onCompleted: (res) => {
      if (isAuthenticated) logout();
      login(res.acceptSubuserInvite.token, res.acceptSubuserInvite.user);
      navigate('/dashboard', { replace: true });
    },
    onError: (err) => setError(err.message),
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (password.length < 8) { setError('The password must be at least 8 characters.'); return; }
    if (password !== repeat) { setError('The two passwords are not the same.'); return; }
    setError('');
    accept({ variables: { token, password } });
  };

  if (checking) {
    return (
      <AuthShell title="Join your team">
        <p className="text-sm text-on-surface-variant">Checking your invitation…</p>
      </AuthShell>
    );
  }

  if (!token || checkError || !invite) {
    return (
      <AuthShell title="This invitation does not work">
        <div className={`${styles.notice} ${styles.noticeBad}`} role="alert">
          <Icon name="link_off" />
          <p>
            {checkError
              ? `Could not check the invitation: ${checkError.message}`
              : 'The invitation is invalid, was already used, or is older than 7 days. Ask the person who invited you to send it again.'}
          </p>
        </div>
        <div className="mt-6 text-center">
          <Link to="/login" className={styles.link}>Go to log in</Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title={`Join ${invite.companyName}`}
      subtitle={`${invite.inviterName} added ${invite.email} to their Infrio account. Choose a password to sign in.`}
    >
      {error && (
        <div className="mb-4 p-3 bg-error-container rounded-lg flex items-center gap-2 text-sm text-on-error-container" role="alert">
          <Icon name="error" className="text-base" />
          {error}
        </div>
      )}
      <form className="space-y-5" onSubmit={handleSubmit}>
        <div className="space-y-2">
          <label className={styles.label} htmlFor="invite-password">Password</label>
          <div className={styles.inputWrapper}>
            <input
              className={styles.input}
              id="invite-password"
              type={show ? 'text' : 'password'}
              autoComplete="new-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button type="button" className={styles.passwordToggle} onClick={() => setShow(!show)} aria-label={show ? 'Hide password' : 'Show password'}>
              <Icon name={show ? 'visibility_off' : 'visibility'} className="text-lg" />
            </button>
          </div>
        </div>
        <div className="space-y-2">
          <label className={styles.label} htmlFor="invite-repeat">Repeat password</label>
          <input
            className={styles.input}
            id="invite-repeat"
            type={show ? 'text' : 'password'}
            autoComplete="new-password"
            required
            value={repeat}
            onChange={(e) => setRepeat(e.target.value)}
          />
        </div>
        <button
          type="submit"
          disabled={saving}
          className="w-full py-3.5 btn-primary rounded-lg shadow-md shadow-primary/20 disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {saving ? 'Joining…' : 'Join and sign in'}
        </button>
      </form>
    </AuthShell>
  );
};

export default AcceptInvite;
